import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { Box, Link, Stack } from '@mui/material';
import WizardTool from './WizardTool';
import { useSelectionWizard } from './SelectionWizardContext';
import { useTokens } from '../../tokens/TokensProvider';
import { useConversationStore } from '../../ConversationStoreProvider';
import { getLatestCompletedSelectionWizardState, getLatestCompletedSelectionWizardWizardId, getSelectionWizardComposerPlacement, } from './selectionWizardComposerUtils';
const selectionWizardProgressStorageKey = (conversationId, toolCallId) => (`mcn.selectionWizard.progress.${conversationId}.${toolCallId}`);
/**
 * Bottom composer: while selectionWizard is pending or user is editing, replaces chat input with WizardTool.
 * Edit reuses the same inline flow and pre-fills from the last completed tool result.
 * After localStorage restore, a pending selectionWizard tool call (no result yet) re-opens the wizard
 * and completion/cancel submits via `submitSelectionWizardToolResult`.
 */
const SelectionWizardComposerArea = ({ messages, append, chatInput, submitSelectionWizardToolResult, fillsConversationArea = false, }) => {
    const tokens = useTokens();
    const store = useConversationStore();
    const conversationId = store.currentConversation?.id ?? '';
    const { pendingInlineWizard, pendingComposerEdit, resolvePendingInlineWizard, resolveComposerEditWizard, updateWizardProgress, setResumedInlineToolCallId, requestEditWizard, wizardsWithSteps, markToolCallResolved, isToolCallResolved, } = useSelectionWizard();
    const placement = useMemo(() => getSelectionWizardComposerPlacement(messages, pendingInlineWizard, wizardsWithSteps.length, isToolCallResolved), [messages, pendingInlineWizard, wizardsWithSteps.length, isToolCallResolved]);
    const resumedProgress = useMemo(() => {
        if (!placement || pendingInlineWizard || !conversationId || typeof sessionStorage === 'undefined') {
            return null;
        }
        try {
            const raw = sessionStorage.getItem(selectionWizardProgressStorageKey(conversationId, placement.toolCallId));
            if (!raw)
                return null;
            const parsed = JSON.parse(raw);
            return {
                initialStepIndex: typeof parsed.stepIndex === 'number' ? parsed.stepIndex : undefined,
                initialAnswers: parsed.answers && typeof parsed.answers === 'object' && !Array.isArray(parsed.answers)
                    ? parsed.answers
                    : undefined,
            };
        }
        catch {
            return null;
        }
    }, [placement, pendingInlineWizard, conversationId]);
    useEffect(() => {
        if (placement && !pendingInlineWizard) {
            setResumedInlineToolCallId(placement.toolCallId);
            return () => setResumedInlineToolCallId(null);
        }
        setResumedInlineToolCallId(null);
        return undefined;
    }, [placement, pendingInlineWizard, setResumedInlineToolCallId]);
    const clearResumeProgressStorage = useCallback((toolCallId) => {
        if (!conversationId || typeof sessionStorage === 'undefined')
            return;
        try {
            sessionStorage.removeItem(selectionWizardProgressStorageKey(conversationId, toolCallId));
        }
        catch {
            /* ignore quota / private mode */
        }
    }, [conversationId]);
    const toolCallWizardUi = useMemo(() => {
        if (pendingComposerEdit || !placement || wizardsWithSteps.length === 0)
            return null;
        const wizardId = placement.wizardId ?? wizardsWithSteps[0]?.id;
        const found = wizardsWithSteps.find((w) => w.id === wizardId);
        const selectedWizard = found ?? wizardsWithSteps[0];
        const steps = selectedWizard?.steps ?? [];
        const tid = placement.toolCallId;
        const liveToolCall = Boolean(pendingInlineWizard);
        const persistProgress = (stepIdx, answers) => {
            if (typeof sessionStorage === 'undefined' || !conversationId)
                return;
            try {
                sessionStorage.setItem(selectionWizardProgressStorageKey(conversationId, tid), JSON.stringify({ stepIndex: stepIdx, answers }));
            }
            catch {
                /* ignore */
            }
        };
        const finishInlineWizard = (result) => {
            const wid = result?.wizardId ?? selectedWizard?.id ?? 'default';
            const toolResult = result
                ? { summary: result.summary, answers: result.answers, wizardId: wid }
                : null;
            // Mark resolved first so placement never rebinds this tool call after we clear the live promise.
            markToolCallResolved(tid, toolResult ?? undefined);
            if (toolResult) {
                // Live tool call: resolving the promise lets the SDK write the result and continue.
                // Resumed call (after reload): no live promise, so submit via addToolResult.
                if (liveToolCall) {
                    resolvePendingInlineWizard(toolResult);
                }
                else {
                    submitSelectionWizardToolResult?.(tid, toolResult);
                }
            }
            else if (liveToolCall) {
                resolvePendingInlineWizard(null);
            }
            else {
                submitSelectionWizardToolResult?.(tid, {
                    cancelled: true,
                    message: 'User closed the wizard without completing.',
                });
            }
            clearResumeProgressStorage(tid);
        };
        return (_jsx(Box, { sx: {
                width: '100%',
                py: fillsConversationArea ? 1 : 0.5,
                display: 'flex',
                flexDirection: 'column',
                flex: fillsConversationArea ? 1 : '0 1 auto',
                minHeight: 0,
                maxHeight: '100%',
                minWidth: 0,
                overflow: 'hidden',
                justifyContent: 'flex-start',
            }, children: _jsx(WizardTool, { steps: steps.length > 0 ? steps : undefined, initialAnswers: resumedProgress?.initialAnswers, initialStepIndex: resumedProgress?.initialStepIndex, fillAvailableHeight: fillsConversationArea, onProgress: (stepIdx, totalSteps, answers) => {
                    updateWizardProgress(tid, stepIdx, totalSteps, answers);
                    persistProgress(stepIdx, answers);
                }, onComplete: (summary, answers) => {
                    finishInlineWizard({ summary, answers, wizardId: wizardId ?? selectedWizard?.id ?? 'default' });
                }, onClose: () => finishInlineWizard(null) }, `tool-${tid}`) }));
    }, [
        pendingComposerEdit,
        placement,
        wizardsWithSteps,
        pendingInlineWizard,
        resolvePendingInlineWizard,
        updateWizardProgress,
        submitSelectionWizardToolResult,
        markToolCallResolved,
        resumedProgress,
        conversationId,
        clearResumeProgressStorage,
        fillsConversationArea,
    ]);
    const editWizardUi = useMemo(() => {
        if (!pendingComposerEdit || wizardsWithSteps.length === 0)
            return null;
        const { sessionId, wizardId, initialAnswers } = pendingComposerEdit;
        const found = wizardsWithSteps.find((w) => w.id === wizardId);
        const selectedWizard = found ?? wizardsWithSteps[0];
        const steps = selectedWizard?.steps ?? [];
        return (_jsx(Box, { sx: {
                width: '100%',
                py: fillsConversationArea ? 1 : 0.5,
                display: 'flex',
                flexDirection: 'column',
                flex: fillsConversationArea ? 1 : '0 1 auto',
                minHeight: 0,
                maxHeight: '100%',
                minWidth: 0,
                overflow: 'hidden',
                justifyContent: 'flex-start',
            }, children: _jsx(WizardTool, { steps: steps.length > 0 ? steps : undefined, initialAnswers: initialAnswers, fillAvailableHeight: fillsConversationArea, onProgress: (stepIdx, totalSteps, answers) => {
                    updateWizardProgress(sessionId, stepIdx, totalSteps, answers);
                }, onComplete: (summary, answers) => {
                    resolveComposerEditWizard({
                        summary,
                        answers,
                        wizardId,
                    });
                }, onClose: () => resolveComposerEditWizard(null) }, sessionId) }));
    }, [
        pendingComposerEdit,
        resolveComposerEditWizard,
        updateWizardProgress,
        wizardsWithSteps,
        fillsConversationArea,
    ]);
    const composerWizardUi = toolCallWizardUi ?? editWizardUi;
    const completedState = useMemo(() => getLatestCompletedSelectionWizardState(messages), [messages]);
    const editTargetWizardId = useMemo(() => getLatestCompletedSelectionWizardWizardId(messages), [messages]);
    const editLabel = useMemo(() => {
        const fromToken = tokens.getValue('editSelectionWizardLabel');
        if (fromToken && fromToken.trim() && fromToken !== '{{editSelectionWizardLabel}}') {
            return fromToken;
        }
        const wizard = editTargetWizardId
            ? wizardsWithSteps.find((w) => w.id === editTargetWizardId)
            : undefined;
        const name = wizard?.name?.trim();
        if (name)
            return `Edit ${name}`;
        return 'Edit your selections';
    }, [tokens, editTargetWizardId, wizardsWithSteps]);
    const showEditLink = Boolean(!composerWizardUi
        && editTargetWizardId
        && requestEditWizard
        && wizardsWithSteps.some((w) => w.id === editTargetWizardId));
    const handleEditClick = useCallback(async () => {
        if (!requestEditWizard || !editTargetWizardId)
            return;
        const prefill = completedState?.wizardId === editTargetWizardId ? completedState.answers : {};
        const result = await requestEditWizard(editTargetWizardId, prefill);
        if (result?.summary)
            append({ role: 'user', content: result.summary });
    }, [requestEditWizard, editTargetWizardId, completedState, append]);
    return (_jsxs(Stack, { direction: "column", spacing: 0.5, sx: {
            width: '100%',
            minHeight: 0,
            maxHeight: '100%',
            flex: fillsConversationArea ? 1 : '0 1 auto',
            overflow: 'hidden',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
        }, children: [showEditLink && (_jsx(Box, { sx: { px: 0.5 }, children: _jsx(Link, { component: "button", type: "button", variant: "body2", onClick: handleEditClick, sx: { cursor: 'pointer', textAlign: 'left' }, children: editLabel }) })), composerWizardUi ?? chatInput] }));
};
export default SelectionWizardComposerArea;
//# sourceMappingURL=SelectionWizardComposerArea.js.map